import type { Cycle } from "./types";
import { TRAIN_DRIVER_V1_CYCLE } from "./trainDriverV1";

/** Built-in and company onboarding cycles available for selection. */
export const AVAILABLE_CYCLES: Cycle[] = [TRAIN_DRIVER_V1_CYCLE];

export function getCycleById(id: string): Cycle | undefined {
  return AVAILABLE_CYCLES.find((c) => c.id === id);
}

export type FrameworkCycleOption = { id: string; name: string; framework_cycle_id?: string | null };

/**
 * Build Framework / onboarding "Add cycle" options from company cycles + static templates.
 * When the company already has built cycles, only those are listed (so a custom title like
 * "Train Driver v2" is what appears — not a duplicate static "Train Driver v1" template).
 */
export function mergeFrameworkCycleOptions(
  rows: Array<{
    id?: string | number;
    title?: string;
    framework_cycle_id?: string | null;
    expiry_years?: number | null;
    expiry_months?: number | null;
    company_code?: string | null;
  }>,
  companyCode?: string
): FrameworkCycleOption[] {
  const want = String(companyCode ?? "").trim().toLowerCase();
  const staticIds = new Set(AVAILABLE_CYCLES.map((c) => c.id));
  const companyRows = (Array.isArray(rows) ? rows : []).filter((row) => {
    const id = String(row?.id ?? "").trim();
    if (!id) return false;
    // Never list the static library ids as "company cycles".
    if (staticIds.has(id)) return false;
    if (!want) return true;
    const cc = String(row?.company_code ?? "").trim();
    // Prefer explicit company ownership when present.
    if (cc) return cc.toLowerCase() === want;
    return true;
  });

  const toOptions = (
    source: Array<{
      id?: string | number;
      title?: string;
      expiry_years?: number | null;
      framework_cycle_id?: string | null;
    }>
  ): FrameworkCycleOption[] => {
    const byId = new Map<string, FrameworkCycleOption>();
    for (const row of source) {
      const id = String(row.id ?? "").trim();
      if (!id) continue;
      const name = (typeof row.title === "string" ? row.title.trim() : "") || id;
      byId.set(id, {
        id,
        name,
        framework_cycle_id:
          typeof row.framework_cycle_id === "string" && row.framework_cycle_id.trim()
            ? row.framework_cycle_id.trim()
            : null,
      });
    }
    const options = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
    const nameCounts = new Map<string, number>();
    for (const opt of options) nameCounts.set(opt.name, (nameCounts.get(opt.name) || 0) + 1);
    return options.map((opt) => {
      if ((nameCounts.get(opt.name) || 0) < 2) return opt;
      const row = source.find((r) => String(r.id).trim() === opt.id);
      if (!row) return { ...opt, name: `${opt.name} (${opt.id})` };
      const monthsRaw = Number(row.expiry_months);
      const months =
        Number.isInteger(monthsRaw) && monthsRaw >= 1
          ? monthsRaw
          : Number.isFinite(Number(row.expiry_years))
            ? Math.trunc(Number(row.expiry_years)) * 12
            : null;
      if (months != null) {
        const label =
          months % 12 === 0 ? `${months / 12} yr` : months < 12 ? `${months} mo` : `${Math.floor(months / 12)} yr ${months % 12} mo`;
        return { ...opt, name: `${opt.name} (${label})` };
      }
      return { ...opt, name: `${opt.name} (${opt.id})` };
    });
  };

  if (companyRows.length > 0) {
    return toOptions(companyRows);
  }

  return AVAILABLE_CYCLES.map((c) => ({ id: c.id, name: c.name }));
}

/** Suggest a company-specific title when a cycle still carries the global template name. */
export function suggestCompanyCycleTitle(templateName: string): string {
  const name = String(templateName ?? "").trim();
  if (!name) return "Company cycle";
  const m = name.match(/^(.*?)(\s+)[vV](\d+)$/);
  if (m) return `${m[1]}${m[2]}v${Number(m[3]) + 1}`;
  return `${name} (company)`;
}

export { TRAIN_DRIVER_V1_CYCLE } from "./trainDriverV1";
export type { Cycle, CycleUnit, CycleElement, GradingLevel } from "./types";
export { GRADING_LEVELS } from "./types";
